import { useCallback, useMemo, useRef } from 'react'
import { useNavigate } from '@reach/router'
import { QueryObserverResult } from 'react-query'
import Breadcrumb from 'src/components/shared/Breadcrumb/Breadcrumb'
import AsyncEnhancedTable from 'src/components/shared/EnhancedTable/AsyncEnhancedTable'
import MainContentPaper from 'src/components/shared/ui/MainContentPaper'
import useStyles from 'src/components/views/Messages/hooks/useStyles'
import { ESCRITOS_LEXNET } from 'src/constants/routes'
import { useTipoMensajeContext } from 'src/contexts/TipoMensajeContext'
import { Escrito, EscritoLegal } from 'src/interfaces/EscritosLegales'
import { GetEscritosResponse } from 'src/services/EscritosServices'

type Props = {
  onChangePaginationOptions: ({
    pageIndex,
    pageSize
  }: {
    pageIndex: string
    pageSize: string
  }) => void
  messagesQuery: QueryObserverResult<GetEscritosResponse>
  subsection: string
  pageSize: number
}

export default function EscritosView({
  onChangePaginationOptions,
  messagesQuery,
  subsection,
  pageSize
}: Props): JSX.Element {
  const navigate = useNavigate()
  const classes = useStyles()
  const pageCount = useRef<number | null>(null)
  const { getRoute } = useTipoMensajeContext()
  const columns = useMemo(
    () => [
      {
        id: 'idEnvio',
        Header: 'Identificador Lexnet',
        accessor: 'idEnvio',
        width: 40
      },
      {
        id: 'asunto',
        Header: 'Asunto',
        accessor: (escrito: EscritoLegal) => {
          const escritoData = JSON.parse(escrito.data)
          if (escritoData && escritoData.asunto) {
            return escritoData.asunto
          }
          if (escritoData && escritoData.procedimiento && escritoData.procedimiento.procedimiento) {
            return `${escritoData.procedimiento.procedimiento.texto} [${escritoData.procedimiento.procedimiento.codigo}]`
          }
          return ''
        },
        width: 80
      },
      {
        id: 'descripcion',
        Header: 'Descripcion',
        accessor: 'descripcion',
        width: 100
      },
      {
        id: 'estado',
        Header: 'Estado',
        accessor: (escrito: EscritoLegal) => escrito.estado.descripcion,
        width: 100
      }
    ],
    // eslint-disable-next-line
    [classes.initial, classes.signed]
  )

  const handleClickedRow = useCallback(
    function(row) {
      const { data, escritoData } = row.original
      let escritoJson = data

      if (escritoData && escritoData.length) {
        escritoJson = escritoData
      }

      const escrito: Escrito = JSON.parse(escritoJson)

      escrito.id = row.original.id
      escrito.descripcion = row.original.descripcion
      escrito.tipo = row.original.tipo
      escrito.tipoEscrito = row.original.tipoEscrito
      escrito.tipoMensaje = row.original.tipoEscrito.codigo

      const route = getRoute(escrito.tipoMensaje)

      navigate(ESCRITOS_LEXNET(route), { state: { escrito } })
    },
    // eslint-disable-next-line
    [navigate]
  )

  if (messagesQuery.isSuccess) {
    pageCount.current = Math.ceil(messagesQuery.data.total / pageSize)
  }

  return (
    <section>
      <Breadcrumb section="ESCRITOS" subsection={subsection} />
      <MainContentPaper>
        <AsyncEnhancedTable
          columns={columns}
          data={(messagesQuery.data && messagesQuery.data.results) ?? []}
          onChangePaginationOptions={onChangePaginationOptions}
          pageCount={pageCount.current ?? 0}
          pageSize={pageSize}
          totalRowsCount={messagesQuery.data?.total ?? 0}
          isFetching={messagesQuery.isLoading}
          isSuccess={messagesQuery.isSuccess}
          isError={messagesQuery.isError}
          onClickRow={handleClickedRow}
        />
      </MainContentPaper>
    </section>
  )
}
